import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { DatabaseService } from '../../services/DatabaseService';
import LobbyView from './LobbyView';
import { useAuth } from '../../contexts/AuthContext';
import VoteSecurity from '../../utils/VoteSecurity';

const VotePage = () => {
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [participantId, setParticipantId] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [alreadyVoted, setAlreadyVoted] = useState(false);
  
  const { pollId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Check if the user has already voted in this poll
  useEffect(() => {
    if (pollId) {
      const hasVoted = VoteSecurity.hasVoted(pollId);
      setAlreadyVoted(hasVoted);
    }
  }, [pollId]);
  
  // Fetch poll data
  useEffect(() => {
    const fetchPoll = async () => {
      try {
        console.log('Fetching poll data for ID:', pollId);
        const pollData = await DatabaseService.getPoll(pollId);
        console.log('Poll data retrieved:', pollData);
        setPoll(pollData);
        
        // If poll is not active, redirect to results
        if (!pollData.isActive || (pollData.expiresAt && new Date(pollData.expiresAt) < new Date())) {
          console.log('Poll is not active, redirecting to results');
          navigate(`/polls/${pollId}`);
          return;
        }
        
        // If user has already voted, they can still view the lobby but won't be able to vote again
        if (alreadyVoted) {
          console.log('User has already voted in this poll');
          // We still allow them to join/view the lobby
        }
        
        // Prompt for name if not logged in
        if (!currentUser) {
          console.log('User not logged in, showing name prompt');
          setShowNamePrompt(true);
        } else {
          // Auto-join with user info for logged in users
          console.log('User logged in, auto-joining with user info');
          joinPoll(currentUser.displayName || currentUser.email);
        }
      } catch (err) {
        console.error('Failed to load poll:', err);
        setError('Failed to load poll: ' + (err.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchPoll();
  }, [pollId, currentUser, navigate, alreadyVoted]);
  
  // Function to join the poll/lobby
  const joinPoll = async (name) => {
    try {
      setError(''); // Clear previous errors
      console.log('Attempting to join poll with name:', name);
      
      // Check for existing participant record in case they've joined before
      // but browser was refreshed/closed
      const existingParticipantId = localStorage.getItem(`participant_${pollId}`);
      if (existingParticipantId) {
        console.log('Found existing participant ID:', existingParticipantId);
        setParticipantId(existingParticipantId);
        setShowNamePrompt(false);
        return;
      }
      
      const userInfo = {
        userId: currentUser ? currentUser.uid : null,
        name: name || 'Anonymous Participant'
      };
      
      console.log('User info for join:', userInfo);
      
      // Create participant document
      const { participantId } = await DatabaseService.joinPoll(pollId, userInfo);
      console.log('Successfully joined poll with participantId:', participantId);
      
      // Store the participant ID in localStorage for persistence
      localStorage.setItem(`participant_${pollId}`, participantId);
      
      setParticipantId(participantId);
      setShowNamePrompt(false);
    } catch (err) {
      console.error('Failed to join poll:', err);
      
      // More specific error messages based on the error
      if (err.message && err.message.includes('permission')) {
        setError('Failed to join poll: Permission denied. This might be due to Firebase security rules.');
      } else if (err.message && err.message.includes('not found')) {
        setError('Failed to join poll: Poll not found or no longer available.');
      } else if (err.message && err.message.includes('no longer active')) {
        setError('This poll is no longer active.');
        // Redirect to results after a delay
        setTimeout(() => navigate(`/polls/${pollId}`), 3000);
      } else {
        setError(`Failed to join poll: ${err.message || 'Unknown error'}`);
      }
    }
  };
  
  const handleNameSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!nameInput || nameInput.trim().length === 0) {
      setError('Please enter a name or click "Join Anonymously"');
      return;
    }
    
    if (nameInput.trim().length > 50) {
      setError('Name must be 50 characters or less');
      return;
    }
    
    joinPoll(nameInput.trim());
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
        <div className="mt-4">
          <Link to="/" className="text-indigo-600 hover:text-indigo-900">
            &larr; Back to Home
          </Link>
        </div>
      </div>
    );
  }
  
  if (!poll) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">Poll not found</span>
        </div>
        <div className="mt-4">
          <Link to="/" className="text-indigo-600 hover:text-indigo-900">
            &larr; Back to Home
          </Link>
        </div>
      </div>
    );
  }
  
  // Name prompt modal
  if (showNamePrompt) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 max-w-md w-full">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Join Poll</h2>
          <p className="text-gray-600 mb-6">
            Enter your name to join the poll. This will be visible to other participants.
          </p>
          
          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          
          <form onSubmit={handleNameSubmit}>
            <div className="mb-4">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Your Name
              </label>
              <input
                type="text"
                id="name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder="Enter your name"
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => joinPoll('')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Join Anonymously
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
              >
                Join Poll
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
  
  // Check if user is the host
  const isHost = currentUser && poll.createdBy === currentUser.uid;
  
  // If user has joined, show the lobby
  return <LobbyView poll={poll} isHost={isHost} participantId={participantId} />;
};

export default VotePage;